import {
  interpolateText,
  parseTemplateVariables,
  replaceTemplateVariables,
  type TemplateContext,
} from "@tellescope/utils";
import type { AsyncResolverDeps, SeasonContext } from "./types";
import { ASYNC_RESOLVERS, ASYNC_SOURCES, isAsyncSource } from "./asyncResolvers";

/**
 * Returns true if the text contains any async placeholders (e.g. {{season.token}}).
 * Used to decide whether a link's href needs click-time resolution.
 */
export const hasAsyncPlaceholders = (text: string | undefined | null): boolean => {
  if (!text) return false;
  return parseTemplateVariables(text).some((v) => isAsyncSource(v.source));
};

export interface InterpolateAsyncOptions {
  /**
   * When true (default), click-time substitutions (async sources and seasonContext
   * lookups) are wrapped with `encodeURIComponent`. URL-context callers should
   * keep this on; non-URL callers can opt out by passing `false`.
   */
  encode?: boolean;
}

/**
 * Async interpolation pass for click-time resolution. Sync sources are resolved
 * first via `interpolateText`, then `season.<field>` placeholders are resolved:
 * - If `seasonContext[field]` is defined, use that value (e.g. partner banner).
 * - Else if the field is in the async registry (e.g. `token`), call the resolver.
 * - Else empty string.
 *
 * Resolutions are memoized per `source.field` so e.g. `{{season.token}}` mints
 * exactly once even when used multiple times in the same string.
 *
 * Throws if any async resolver rejects — callers own UX (toast, fallback, etc.).
 */
export const interpolateTextAsync = async (
  text: string | undefined | null,
  context: TemplateContext,
  deps: AsyncResolverDeps,
  seasonContext?: SeasonContext,
  options?: InterpolateAsyncOptions
): Promise<string> => {
  if (!text) return "";

  const encode = options?.encode ?? true;
  const syncResolved = interpolateText(text, context, { deferredSources: ASYNC_SOURCES });

  const clickTimeKeys = parseTemplateVariables(syncResolved).reduce<Map<string, { source: string; field: string }>>(
    (acc, v) =>
      isAsyncSource(v.source) ? acc.set(`${v.source}.${v.field}`, { source: v.source, field: v.field }) : acc,
    new Map()
  );

  if (clickTimeKeys.size === 0) return syncResolved;

  const resolutions = new Map<string, string>();
  await Promise.all(
    Array.from(clickTimeKeys.entries()).map(async ([key, { source, field }]) => {
      // `season.token` is the only field that hits the async resolver. Everything
      // else under `season.*` comes from the click-time `seasonContext` (e.g.
      // partner banner from the org's saved config). Unknown fields → empty string.
      if (source === "season" && field !== "token") {
        resolutions.set(key, seasonContext?.[field] ?? "");
        return;
      }
      if (!isAsyncSource(source)) {
        resolutions.set(key, "");
        return;
      }
      const value = await ASYNC_RESOLVERS[source](field, deps);
      resolutions.set(key, value);
    })
  );

  return replaceTemplateVariables(syncResolved, (source, field) => {
    const resolved = resolutions.get(`${source}.${field}`);
    if (resolved === undefined) return undefined;
    return encode ? encodeURIComponent(resolved) : resolved;
  });
};
